import { Divider, Grid, Typography } from '@mui/material';

import { useFormikContext } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import InfoBox from 'app/components/InfoBox';
import { translations } from 'locales/i18n';
import { JobOffering, JobPeriod } from 'types/AppTypes';
import { validateForm } from 'utils/FormErrorHelper';
import { isJobPeriodActive } from 'utils/jobPeriodHelper';

import JobOfferPeriod from '../../JobOfferDetail/components/JobOfferPeriod';
import { jobOfferDeactivationModalValidationSchema } from '../validation';

export const VerifyJobPeriods = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { values } = useFormikContext<{
    jobOffering: JobOffering & { jobPeriods: JobPeriod[] };
    jobPeriodIds: number[];
  }>();

  useEffect(() => {
    validateForm(
      values,
      yup.object().shape(jobOfferDeactivationModalValidationSchema(t)),
      dispatch
    );
  }, [dispatch, t, values]);

  // ? Show info that no jobOffering is selected
  if (values.jobOffering === null) {
    return (
      <Typography variant="h6">
        {t(translations.pages.deactivateJobOffers.noJobOfferSelected())}
      </Typography>
    );
  }

  // ? Show info that no jobPeriod is selected
  if (!values.jobPeriodIds.length) {
    return (
      <Typography variant="h6">
        {t(translations.pages.deactivateJobOffers.noJobPeriodSelected())}
      </Typography>
    );
  }

  const activeJobPeriod = values.jobOffering.jobPeriods.find(isJobPeriodActive);
  const inactiveJobPeriods = values.jobOffering.jobPeriods.filter(
    jp => values.jobPeriodIds.includes(jp.id) && jp.id !== activeJobPeriod?.id
  );

  return (
    <Grid container spacing={2}>
      {activeJobPeriod && !!values.jobPeriodIds.includes(activeJobPeriod.id) && (
        <>
          {values.jobOffering.jobType.paid && (
            <Grid item xs={12}>
              <InfoBox
                title={t(translations.pages.deactivateJobOffers.warning.title())}
                type="warning"
              >
                {t(translations.pages.deactivateJobOffers.warning.information())}
              </InfoBox>
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography component={'h2'}>
              {t(translations.pages.deactivateJobOffers.jobPeriods.activeJobPeriod())}
            </Typography>
            <Divider />
          </Grid>

          <Grid item xs={12} container spacing={2}>
            <Grid item xs={12} md={6} lg={4} xl={3} key={`${activeJobPeriod.id}_verify_g`}>
              <JobOfferPeriod
                key={`${activeJobPeriod.id}_verify`}
                selected={false}
                canceled={true}
                period={activeJobPeriod}
                readonly
                companyId={0}
                creatorId={0}
                user={0}
              />
            </Grid>
          </Grid>
        </>
      )}
      {inactiveJobPeriods.length !== 0 && (
        <>
          <Grid item xs={12}>
            <Typography component={'h2'}>
              {t(translations.pages.deactivateJobOffers.jobPeriods.inactiveJobPeriod())}
            </Typography>
            <Divider />
          </Grid>
          <Grid item xs={12} container spacing={2}>
            {inactiveJobPeriods.map(jp => (
              <Grid item xs={12} md={6} lg={4} xl={3} key={`${jp.id}_verify_g`}>
                <JobOfferPeriod
                  key={`${jp.id}_verify`}
                  selected={false}
                  canceled={true}
                  period={jp}
                  readonly
                  companyId={0}
                  creatorId={0}
                  user={0}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Grid>
  );
};
